import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../config/api';
import API_CONFIG from '../config/api';

/**
 * Hook for managing user notifications (bell icon in header)
 * Fetches notifications, handles read status, and provides unread count
 */
export const useNotifications = (options = {}) => {
  const { limit = 10, autoFetch = true } = options;
  
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch notifications from the API
   */
  const fetchNotifications = useCallback(async (fetchOptions = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        limit: fetchOptions.limit || limit,
        unreadOnly: fetchOptions.unreadOnly || false,
      });

      const response = await apiClient.get(
        `${API_CONFIG.ENDPOINTS.USER_NOTIFICATIONS}?${params}`
      );

      const data = response.data;
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError(err.response?.data?.error || 'Failed to load notifications');
      // Keep existing data on error
    } finally {
      setLoading(false);
    }
  }, [limit]);

  /**
   * Get just the unread count (lightweight call for badge)
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiClient.get(
        API_CONFIG.ENDPOINTS.USER_NOTIFICATIONS_UNREAD_COUNT
      );
      setUnreadCount(response.data.count || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, []);

  /**
   * Mark a single notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await apiClient.post(
        API_CONFIG.ENDPOINTS.USER_NOTIFICATION_READ(notificationId)
      );

      // Update local state optimistically
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      return { success: true };
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      return { success: false, error: err.response?.data?.error };
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await apiClient.post(
        API_CONFIG.ENDPOINTS.USER_NOTIFICATIONS_READ_ALL
      );

      // Update local state
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read: true }))
      );
      setUnreadCount(0);

      return { success: true, markedCount: response.data.markedCount };
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      return { success: false, error: err.response?.data?.error };
    }
  }, []);

  // Fetch notifications on mount if autoFetch is enabled
  useEffect(() => {
    if (autoFetch) {
      fetchNotifications();
    }
  }, [autoFetch, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  };
};

export default useNotifications;

